import { useEffect, useMemo, useRef, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  fetchProductivityAdjustments,
  fetchProductivityDashboardSummary,
  fetchProductivityDashboardDrilldown,
  exportProductivityDashboardDrilldown,
  fetchProductivityCompanyOptions,
  fetchProductivityDepartmentOptions,
  fetchProductivityUserOptions,
  resubmitProductivityAdjustment,
  fetchAppActivity,
  fetchDomainActivity,
  fetchAmbientRedDomains,
} from '../../api/productivityApi'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function sevenDaysAgoIso() {
  const date = new Date()
  date.setDate(date.getDate() - 6)
  return date.toISOString().slice(0, 10)
}

export function useProductivityDashboard() {
  const ACTIVITY_PAGE_SIZE = 5
  const ADJUSTMENTS_PAGE_SIZE = 5

  const [userId, setUserId] = useState('all')
  const [departmentId, setDepartmentId] = useState('all')
  const [companyId, setCompanyId] = useState('all')
  const [dateFrom, setDateFrom] = useState(sevenDaysAgoIso())
  const [dateTo, setDateTo] = useState(todayIso())

  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Server-side App Activity Table state
  const [appPage, setAppPage] = useState(1)
  const [appTable, setAppTable] = useState({ items: [], page: 1, pageSize: ACTIVITY_PAGE_SIZE, total: 0 })
  const [appLoading, setAppLoading] = useState(false)

  // Server-side Domain Activity Table state
  const [domainPage, setDomainPage] = useState(1)
  const [domainFilter, setDomainFilter] = useState('all')
  const [domainTable, setDomainTable] = useState({ items: [], page: 1, pageSize: ACTIVITY_PAGE_SIZE, total: 0 })
  const [domainLoading, setDomainLoading] = useState(false)

  // Server-side Ambient Red Domains Table state
  const [ambientPage, setAmbientPage] = useState(1)
  const [ambientTable, setAmbientTable] = useState({ items: [], page: 1, pageSize: ACTIVITY_PAGE_SIZE, total: 0 })
  const [ambientLoading, setAmbientLoading] = useState(false)

  // Server-side Adjustments Table state
  const [adjustmentsPage, setAdjustmentsPage] = useState(1)
  const [adjustmentsTable, setAdjustmentsTable] = useState({ items: [], page: 1, pageSize: ADJUSTMENTS_PAGE_SIZE, hasMore: false, total: 0 })
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false)
  const [adjustmentActionMessage, setAdjustmentActionMessage] = useState('')
  const [adjustmentActionError, setAdjustmentActionError] = useState('')
  const [adjustmentActionId, setAdjustmentActionId] = useState(null)
  const [adjustmentsRefreshTick, setAdjustmentsRefreshTick] = useState(0)

  const [userOptions, setUserOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [drill, setDrill] = useState({
    open: false,
    level: '',
    title: '',
    rows: [],
    columns: [],
    breadcrumb: [],
    total: 0,
    page: 1,
    pageSize: 25,
    loading: false,
    error: '',
    dimensions: {},
    calculationBasis: '',
  })
  const drillRequestId = useRef(0)

  const dashboardFilterParams = () => ({
    user_id: userId && userId !== 'all' ? Number(userId) : undefined,
    department_id: departmentId && departmentId !== 'all' ? Number(departmentId) : undefined,
    company_id: companyId && companyId !== 'all' ? Number(companyId) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  })

  async function loadDrill(level, dimensions = {}, page = 1) {
    const requestId = ++drillRequestId.current
    const effectiveDimensions = Object.keys(dimensions).length ? dimensions : (drill.dimensions || {})
    setDrill((prev) => ({ ...prev, open: true, level, loading: true, error: '', page, dimensions: effectiveDimensions }))
    try {
      const result = await fetchProductivityDashboardDrilldown({
        ...dashboardFilterParams(),
        ...effectiveDimensions,
        level,
        page,
        page_size: 25,
      })
      if (requestId !== drillRequestId.current) return
      setDrill({
        open: true,
        level: result.level || level,
        title: result.title || 'Productivity details',
        rows: Array.isArray(result.rows) ? result.rows : [],
        columns: Array.isArray(result.columns) ? result.columns : [],
        breadcrumb: Array.isArray(result.breadcrumb) ? result.breadcrumb : [],
        total: Number(result.total || 0),
        page: Number(result.page || page),
        pageSize: Number(result.pageSize || 25),
        loading: false,
        error: '',
        dimensions: effectiveDimensions,
        calculationBasis: result.calculationBasis || '',
      })
    } catch (requestError) {
      if (requestId !== drillRequestId.current) return
      setDrill((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: extractApiErrorMessage(requestError, 'Failed to load productivity details.'),
      }))
    }
  }

  function closeDrill() {
    drillRequestId.current += 1
    setDrill((prev) => ({ ...prev, open: false }))
  }

  function resetDrill() {
    closeDrill()
  }

  async function exportDrill(format = 'csv') {
    const { blob, filename } = await exportProductivityDashboardDrilldown({
      ...dashboardFilterParams(),
      level: drill.level,
      format,
      ...(drill.dimensions || {}),
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  // 1. Fetch Companies once on mount
  useEffect(() => {
    let active = true
    async function loadCompanies() {
      try {
        const companies = await fetchProductivityCompanyOptions()
        if (!active) return
        setCompanyOptions(companies)
      } catch {
        if (!active) return
        setCompanyOptions([])
      }
    }
    loadCompanies()
    return () => {
      active = false
    }
  }, [])

  // 2. Fetch Departments when companyId changes & reset dependent state
  useEffect(() => {
    let active = true
    async function loadDepartments() {
      try {
        const params = companyId !== 'all' ? { company_ids: companyId } : {}
        const departments = await fetchProductivityDepartmentOptions(params)
        if (!active) return
        setDepartmentOptions(departments)
      } catch {
        if (!active) return
        setDepartmentOptions([])
      }
    }
    
    setDepartmentId('all')
    setUserId('all')

    loadDepartments()
    return () => {
      active = false
    }
  }, [companyId])

  // 3. Fetch Users when companyId or departmentId changes
  useEffect(() => {
    let active = true
    async function loadUsers() {
      try {
        const params = {}
        if (companyId !== 'all') {
          params.company_id = Number(companyId)
        }
        if (departmentId !== 'all') {
          params.department_id = Number(departmentId)
        }
        const users = await fetchProductivityUserOptions(params)
        if (!active) return
        setUserOptions(users)
      } catch {
        if (!active) return
        setUserOptions([])
      }
    }

    setUserId('all')

    loadUsers()
    return () => {
      active = false
    }
  }, [companyId, departmentId])

  useEffect(() => {
    if (userId === 'all') return
    const isValid = userOptions.some((item) => String(item.id) === String(userId))
    if (!isValid) {
      setUserId('all')
    }
  }, [userId, userOptions])

  // Reset pagination when global filters change
  useEffect(() => {
    setAdjustmentsPage(1)
    setAppPage(1)
    setDomainPage(1)
    setAmbientPage(1)
  }, [dateFrom, dateTo, departmentId, companyId, userId])

  useEffect(() => {
    setDomainPage(1)
  }, [domainFilter])

  useEffect(() => {
    setAdjustmentActionMessage('')
    setAdjustmentActionError('')
  }, [dateFrom, dateTo, departmentId, companyId, userId, adjustmentsPage])

  // Summary Metrics & Usage Stacks Loader
  useEffect(() => {
    let active = true

    async function loadSummary() {
      setIsLoading(true)
      setError('')
      try {
        const filters = {
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          company_id: companyId === 'all' ? undefined : Number(companyId),
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
        const dashboardPayload = await fetchProductivityDashboardSummary(filters)
        if (!active) return
        setSummary(dashboardPayload)
      } catch (requestError) {
        if (!active) return
        setSummary(null)
        setError(extractApiErrorMessage(requestError, 'Failed to load productivity dashboard.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadSummary()
    return () => {
      active = false
    }
  }, [dateFrom, dateTo, departmentId, companyId, userId])

  // ── Server-Side Loader: App Activity ──────────────────────────
  useEffect(() => {
    let active = true

    async function loadAppActivity() {
      setAppLoading(true)
      try {
        const payload = await fetchAppActivity({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          company_id: companyId === 'all' ? undefined : Number(companyId),
          page: appPage,
          page_size: ACTIVITY_PAGE_SIZE,
          include_total: true,
        })
        if (!active) return
        setAppTable({
          items: Array.isArray(payload?.items) ? payload.items : [],
          total: Number(payload?.total || 0),
          page: Number(payload?.page || appPage),
          pageSize: Number(payload?.pageSize || ACTIVITY_PAGE_SIZE),
        })
      } catch {
        if (!active) return
        setAppTable({ items: [], total: 0, page: appPage, pageSize: ACTIVITY_PAGE_SIZE })
      } finally {
        if (active) setAppLoading(false)
      }
    }

    loadAppActivity()
    return () => {
      active = false
    }
  }, [appPage, departmentId, companyId, userId, dateFrom, dateTo])

  // ── Server-Side Loader: Domain Activity ────────────────────────
  useEffect(() => {
    let active = true

    async function loadDomainActivity() {
      setDomainLoading(true)
      try {
        const payload = await fetchDomainActivity({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          company_id: companyId === 'all' ? undefined : Number(companyId),
          classification: domainFilter === 'red' ? 'red' : undefined,
          page: domainPage,
          page_size: ACTIVITY_PAGE_SIZE,
          include_total: true,
        })
        if (!active) return
        setDomainTable({
          items: Array.isArray(payload?.items) ? payload.items : [],
          total: Number(payload?.total || 0),
          page: Number(payload?.page || domainPage),
          pageSize: Number(payload?.pageSize || ACTIVITY_PAGE_SIZE),
        })
      } catch {
        if (!active) return
        setDomainTable({ items: [], total: 0, page: domainPage, pageSize: ACTIVITY_PAGE_SIZE })
      } finally {
        if (active) setDomainLoading(false)
      }
    }

    loadDomainActivity()
    return () => {
      active = false
    }
  }, [domainPage, domainFilter, departmentId, companyId, userId, dateFrom, dateTo])

  // ── Server-Side Loader: Ambient Red Domains ────────────────────
  useEffect(() => {
    let active = true

    async function loadAmbientRedDomains() {
      setAmbientLoading(true)
      try {
        const payload = await fetchAmbientRedDomains({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          company_id: companyId === 'all' ? undefined : Number(companyId),
          page: ambientPage,
          page_size: ACTIVITY_PAGE_SIZE,
          include_total: true,
        })
        if (!active) return
        setAmbientTable({
          items: Array.isArray(payload?.items) ? payload.items : [],
          total: Number(payload?.total || 0),
          page: Number(payload?.page || ambientPage),
          pageSize: Number(payload?.pageSize || ACTIVITY_PAGE_SIZE),
        })
      } catch {
        if (!active) return
        setAmbientTable({ items: [], total: 0, page: ambientPage, pageSize: ACTIVITY_PAGE_SIZE })
      } finally {
        if (active) setAmbientLoading(false)
      }
    }

    loadAmbientRedDomains()
    return () => {
      active = false
    }
  }, [ambientPage, departmentId, companyId, userId, dateFrom, dateTo])

  // ── Server-Side Loader: Adjustments ───────────────────────────
  useEffect(() => {
    let active = true

    async function loadAdjustments() {
      setAdjustmentsLoading(true)
      try {
        const payload = await fetchProductivityAdjustments({
          page: adjustmentsPage,
          pageSize: ADJUSTMENTS_PAGE_SIZE,
          includeTotal: true,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          company_id: companyId === 'all' ? undefined : Number(companyId),
        })
        if (!active) return
        setAdjustmentsTable({
          items: Array.isArray(payload?.items) ? payload.items : [],
          page: Number(payload?.page || adjustmentsPage),
          pageSize: Number(payload?.pageSize || ADJUSTMENTS_PAGE_SIZE),
          hasMore: Boolean(payload?.hasMore),
          total: Number(payload?.total || 0),
        })
      } catch {
        if (!active) return
        setAdjustmentsTable({ items: [], page: adjustmentsPage, pageSize: ADJUSTMENTS_PAGE_SIZE, hasMore: false, total: 0 })
      } finally {
        if (active) setAdjustmentsLoading(false)
      }
    }

    loadAdjustments()
    return () => {
      active = false
    }
  }, [adjustmentsPage, dateFrom, dateTo, departmentId, companyId, userId, adjustmentsRefreshTick])

  async function handleResubmitAdjustment(adjustmentId) {
    setAdjustmentActionError('')
    setAdjustmentActionMessage('')
    setAdjustmentActionId(adjustmentId)
    try {
      await resubmitProductivityAdjustment(adjustmentId)
      setAdjustmentActionMessage('Adjustment resubmitted for manager approval.')
      setAdjustmentsRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setAdjustmentActionError(extractApiErrorMessage(requestError, 'Failed to resubmit adjustment.'))
    } finally {
      setAdjustmentActionId(null)
    }
  }

  const derived = useMemo(() => {
    const cards = summary?.cards || {}
    const gauge = summary?.utilizationGauge || {}
    const logoutByReason = summary?.logoutByReason || {}
    const productiveOutputMatrix = summary?.productiveOutputMatrix || {}
    const idleTrendAnalysis = summary?.idleTrendAnalysis || {}
    return {
      utilizationPercent: Number(gauge.utilizationPercent || 0),
      activeDuration: gauge.activeDuration || '0h 00m',
      capacityDuration: gauge.capacityDuration || '0h 00m',
      activeSeconds: Number(summary?.dailyPulse?.activeSeconds || 0),
      idleSeconds: Number(summary?.dailyPulse?.idleSeconds || 0),
      elapsedSeconds: 0,
      avgLoginTime: (cards.avgLoginTime && cards.avgLoginTime !== '-') ? cards.avgLoginTime : null,
      avgLogoutTime: (cards.avgLogoutTime && cards.avgLogoutTime !== '-') ? cards.avgLogoutTime : null,
      totalActiveDuration: cards.totalActiveDuration || '0h 00m',
      shiftStartTime: summary?.dailyPulse?.shiftStartTime || '-',
      shiftEndTime: summary?.dailyPulse?.shiftEndTime || '-',
      firstLogin: summary?.dailyPulse?.firstLogin || '-',
      totalIdle: cards.idleDuration || '0h 00m',
      longestStreak: summary?.dailyPulse?.longestStreak || '0h 00m',
      focusScore: 0,
      productivityScore: Number(gauge.utilizationPercent || 0),
      productiveDuration: cards.productiveDuration || '0h 00m',
      approvedProductiveHours: cards.approvedAdjustmentDuration || cards.approvedProductiveHours || cards.productiveAdjustedDuration || '0h 00m',
      approvedAdjustmentDuration: cards.approvedAdjustmentDuration || cards.approvedProductiveHours || cards.productiveAdjustedDuration || '0h 00m',
      pendingAdjustmentDuration: cards.pendingAdjustmentDuration || cards.pendingProductiveHours || '0h 00m',
      rejectedAdjustmentDuration: cards.rejectedAdjustmentDuration || cards.rejectedProductiveHours || '0h 00m',
      pendingProductiveHours: cards.pendingAdjustmentDuration || cards.pendingProductiveHours || '0h 00m',
      rejectedProductiveHours: cards.rejectedAdjustmentDuration || cards.rejectedProductiveHours || '0h 00m',
      timesheetProgress: summary?.timesheetProgress || {
        score: 0.0,
        scoreLabel: '0%',
        goalLabel: 'DAILY TARGET',
        goalValue: '0.0 / 8.0 hrs',
        progress: 0.0,
        loggedHours: 0.0,
        targetHours: 8.0,
      },
      productiveAdjustedDuration: cards.productiveAdjustedDuration || '0h 00m',
      productivityRatioPercent: Number(cards.productivityRatioPercent || 0),
      productiveVsActivePercent: Number(cards.productiveVsActivePercent || 0),
      trackedActiveSeconds: Number(cards.trackedActiveSeconds || 0),
      totalProductiveSeconds: Number(cards.totalProductiveSeconds || 0),
      totalIdleSeconds: Number(cards.totalIdleSeconds || 0),
      capacitySeconds: Number(cards.capacitySeconds || 0),
      dailyAverageProductiveHours: 0,
      dailyAverageIdleHours: 0,
      workingDays: 0,
      dailyPulse: summary?.dailyPulse || {},
      filters: summary?.filters || {},
      reasonBreakdown: Array.isArray(logoutByReason.segments) ? logoutByReason.segments : [],
      idleTrendDefaultView: String(idleTrendAnalysis.defaultView || 'hourly'),
      idleTrendViews: idleTrendAnalysis.views || {},
      dailyTrend: [],
      weeklyView: [],
      hourlyTrend: [],
      teamStanding: {},
      productiveOutputMatrix: Array.isArray(productiveOutputMatrix.bars) ? productiveOutputMatrix.bars : [],
      usageStacks: {
        appUsage: Array.isArray(summary?.usageStacks?.appUsage) ? summary.usageStacks.appUsage : [],
        redDomainUsage: Array.isArray(summary?.usageStacks?.redDomainUsage) ? summary.usageStacks.redDomainUsage : [],
      },
      appActivityRows: appTable.items,
      appTotal: appTable.total,
      appPage: appTable.page,
      appPageSize: appTable.pageSize,
      appLoading,
      domainActivityRows: domainTable.items,
      domainTotal: domainTable.total,
      domainPage: domainTable.page,
      domainPageSize: domainTable.pageSize,
      domainLoading,
      domainFilter,
      ambientRedDomainRows: ambientTable.items,
      ambientTotal: ambientTable.total,
      ambientPage: ambientTable.page,
      ambientPageSize: ambientTable.pageSize,
      ambientLoading,
      adjustmentsRows: Array.isArray(adjustmentsTable.items) ? adjustmentsTable.items : [],
      adjustmentsPage: Number(adjustmentsTable.page || adjustmentsPage),
      adjustmentsPageSize: Number(adjustmentsTable.pageSize || ADJUSTMENTS_PAGE_SIZE),
      adjustmentsTotal: Number(adjustmentsTable.total || 0),
      adjustmentsHasMore: Boolean(adjustmentsTable.hasMore),
      adjustmentsLoading,
      adjustmentActionMessage,
      adjustmentActionError,
      adjustmentActionId,
    }
  }, [
    summary,
    appTable,
    appLoading,
    domainTable,
    domainLoading,
    domainFilter,
    ambientTable,
    ambientLoading,
    adjustmentsTable,
    adjustmentsPage,
    adjustmentsLoading,
    adjustmentActionMessage,
    adjustmentActionError,
    adjustmentActionId,
  ])

  return {
    state: {
      userId,
      departmentId,
      companyId,
      dateFrom,
      dateTo,
      userOptions,
      departmentOptions,
      companyOptions,
      isLoading,
      error,
      drill,
      summary: derived,
    },
    actions: {
      setUserId,
      setDepartmentId,
      setCompanyId,
      setDateFrom,
      setDateTo,
      setAppPage,
      setDomainPage,
      setDomainFilter,
      setAmbientPage,
      setAdjustmentsPage,
      resubmitAdjustment: handleResubmitAdjustment,
      openDrill: loadDrill,
      closeDrill,
      resetDrill,
      exportDrill,
      setDrillPage: (page) => loadDrill(drill.level, drill.dimensions || {}, page),
    },
  }
}
